import { isClip } from '../../../../types/prospect-media';
import { activate } from "../../../../utils/keyboard-activate";
import { formatVideoDate, formatVideoDuration } from '../../../../utils/video-format';
import type { VideoWithProspect } from '../../../../types/prospect-media';
import { tourAnchor, type TourAnchorId } from '../../../tours/tourAnchors';
import './VideosGrid.css';

/** Tags an element as a tour anchor on the chosen card only; every other card gets nothing. */
const rowAnchor = (index: number, anchorIndex: number, id: TourAnchorId) =>
  index === anchorIndex ? tourAnchor(id) : {};

interface VideosGridProps {
  videos: VideoWithProspect[];
  loading: boolean;
  onVideoClick: (videoId: string) => void;
}

export const VideosGrid = ({
  videos,
  loading,
  onVideoClick,
}: VideosGridProps) => {
  if (loading) {
    return (
      <div className="videos-grid__loading">
        <div className="videos-grid__spinner"></div>
        <p>Loading videos...</p>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="videos-grid__empty">
        <p>No videos found</p>
      </div>
    );
  }

  // The clip badge only exists on clips, so its anchor goes on the first card that is one.
  const firstClipIndex = videos.findIndex(isClip);

  return (
    <div className="videos-grid" {...tourAnchor('video.grid')}>
      {videos.map((video, index) => {
        const prospectName = `${video.prospectFirstName} ${video.prospectLastName}`;
        const duration = formatVideoDuration(video.durationSeconds);

        return (
          <div
            key={video.id}
            className="videos-grid__card"
            {...activate(() => onVideoClick(video.id))}
            {...rowAnchor(index, 0, 'video.row.open')}
          >
            <div className="videos-grid__thumbnail-container">
              {video.thumbnailGcsUrl ? (
                <img
                  src={video.thumbnailGcsUrl}
                  alt={`${prospectName} video`}
                  className="videos-grid__thumbnail"
                />
              ) : (
                <div className="videos-grid__thumbnail-placeholder">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                </div>
              )}

              {/* Clip badge — distinguishes a cut-down clip from the original upload */}
              {isClip(video) && (
                <div
                  className="videos-grid__clip-badge"
                  {...rowAnchor(index, firstClipIndex, 'video.row.clip')}
                >
                  Clip
                </div>
              )}

              {/* Duration badge */}
              {duration && (
                <div className="videos-grid__duration">{duration}</div>
              )}

              {/* Gradient overlay with prospect info */}
              <div className="videos-grid__overlay">
                <div className="videos-grid__info">
                  <p className="videos-grid__prospect-name">{prospectName}</p>
                  {video.title && (
                    <p className="videos-grid__clip-title">{video.title}</p>
                  )}
                  <p className="videos-grid__date">{formatVideoDate(video.createdAt)}</p>
                  {video.uploadedByName && (
                    <p className="videos-grid__uploader">{video.uploadedByName}</p>
                  )}
                  {video.tags && video.tags.length > 0 && (
                    <div className="videos-grid__tags">
                      {video.tags.map((tag) => (
                        <span key={tag.id} className="videos-grid__tag">
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
